import { useCallback, useEffect, useId, useState } from 'react';
import type {
  Contrato,
  Estancia,
  EventoCalendario,
  EvaluacionNioCompleta,
  EvaluacionNioReducida,
  EventoHistorial,
  DocumentoPropiedad,
  EstadoEvento,
  EstadoCobro,
  GastoPropiedad,
  TotalesGastoPropiedad,
  FotoPropiedad,
  Incidencia,
  EjecucionVisita,
  Factura,
  Mantenimiento,
  Party,
  PlantillaResumen,
  PropiedadResumen,
  ProveedorVinculado,
  Proveedor,
  Relacion,
  RequisitosActivacion,
  ResumenFicha,
  TablaNio,
  TarifaEquipo,
  UsuarioSesion,
} from '@matias-asset-os/contracts';
import {
  formatearFechaHora,
  formatearImporteDesdeCents,
  t,
  type Idioma,
} from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { contiene } from '../obras/texto.js';
import {
  ChipNio,
  COLOR_SEMAFORO,
  fechaLarga,
  incidenciaActiva,
  FotoVilla,
  semillaDe,
} from '../componentes.js';
import { PanelCicloVida } from '../componentes/PanelCicloVida.js';
import { PanelContrato } from '../componentes/PanelContrato.js';
import { Modal } from '../componentes/Modal.js';
import { NuevaIncidencia } from '../componentes/NuevaIncidencia.js';
import { PanelNio } from '../componentes/PanelNio.js';
import { PanelRelaciones } from '../componentes/PanelRelaciones.js';
import { TarifasEquipoTabla } from '../componentes/TarifasEquipo.js';
import {
  ModalDocumento,
  ModalMantenimiento,
  ModalProveedor,
  ModalVisita,
} from '../componentes/FormulariosFicha.js';
import { ModalIniciarVisita, PanelEjecucion } from '../componentes/EjecucionVisita.js';
import { ModalEditarVisita } from '../componentes/EditarVisita.js';
import { Icono } from '../componentes/Iconos.js';
import { FacturaDelGasto, SenalFactura } from '../componentes/FacturaDelGasto.js';
import { AccionesCarga, ModalEditarGasto } from '../componentes/EditarCarga.js';

interface Props {
  propiedadId: string;
  usuario: UsuarioSesion;
  idioma: Idioma;
  pestanaInicial?: Pestana;
  onVolver: () => void;
  onAbrirIncidencia?: (incidenciaId: string) => void;
}

const PESTANAS = [
  'resumen',
  'incidencias',
  'visitas',
  'mantenimiento',
  'proveedores',
  'gastos',
  'contratos',
  'historial',
] as const;
type Pestana = (typeof PESTANAS)[number];

/**
 * Ficha de propiedad: resume antes de detallar, y permite operar.
 *
 * Ocho pestañas, como la operación real las pide. Cada bloque sólo se pide al
 * servidor si el rol corresponde y sólo cuando su pestaña está abierta: si el
 * rol no corresponde el servidor responde 403 y la interfaz no llega a tener el
 * dato en ningún momento.
 */
export function FichaPropiedad({
  propiedadId,
  usuario,
  idioma,
  pestanaInicial,
  onVolver,
  onAbrirIncidencia,
}: Props) {
  /*
    Se puede entrar por una pestana que no sea el resumen. Hace falta para venir
    desde «Tareas del dia»: quien toca una visita quiere hacer algo con ella, y
    lo que se hace con una visita esta en la pestana de visitas.
  */
  const [pestana, setPestana] = useState<Pestana>(pestanaInicial ?? 'resumen');
  const [propiedad, setPropiedad] = useState<PropiedadResumen | null>(null);
  const [resumen, setResumen] = useState<ResumenFicha | null>(null);
  const [requisitos, setRequisitos] = useState<RequisitosActivacion | null>(null);
  const [relaciones, setRelaciones] = useState<Relacion[]>([]);
  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [incidencias, setIncidencias] = useState<Incidencia[]>([]);
  const [historial, setHistorial] = useState<EventoHistorial[]>([]);
  const [partes, setPartes] = useState<Party[]>([]);
  const [evaluaciones, setEvaluaciones] = useState<
    Array<EvaluacionNioCompleta | EvaluacionNioReducida>
  >([]);
  const [tabla, setTabla] = useState<TablaNio | null>(null);
  const [usuarios, setUsuarios] = useState<Array<{ id: string; nombre: string; rol: string }>>([]);
  const [responsable, setResponsable] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const idResponsable = useId();

  // Datos por pestaña, cargados a demanda.
  const [estancias, setEstancias] = useState<Estancia[]>([]);
  const [fotos, setFotos] = useState<FotoPropiedad[]>([]);
  const [tarifas, setTarifas] = useState<TarifaEquipo[]>([]);
  const [visitas, setVisitas] = useState<EventoCalendario[] | null>(null);
  /*
    LA VISITA DE LA AGENDA DESDE LA QUE SE EMPIEZA LA LISTA.

    La agenda solo dejaba cambiar el estado a mano —abierta, en curso, cerrada—,
    que es contar lo que paso sin hacerlo. Ahora se pulsa la visita programada y
    se abre su lista de comprobacion; al cerrarla, el evento se marca hecho solo.
  */
  const [visitaDeAgenda, setVisitaDeAgenda] = useState<{
    id: string;
    titulo: string;
    motivo?: string | null;
  } | null>(null);
  /** La visita completada que se esta consultando, si hay alguna. */
  const [abierta, setAbierta] = useState<string | null>(null);
  /** La cita de la agenda que se esta editando: fecha, hora, quien va. */
  const [editandoVisita, setEditandoVisita] = useState<EventoCalendario | null>(null);
  const [mantenimiento, setMantenimiento] = useState<Mantenimiento[] | null>(null);
  const [proveedores, setProveedores] = useState<ProveedorVinculado[] | null>(null);
  const [directorio, setDirectorio] = useState<Proveedor[]>([]);
  const [gastos, setGastos] = useState<{
    gastos: GastoPropiedad[];
    totales: TotalesGastoPropiedad;
  } | null>(null);
  /* El gasto cuya factura se esta mirando. */
  const [factura, setFactura] = useState<GastoPropiedad | null>(null);
  /* Y el que se esta corrigiendo. Solo Direccion; el servidor lo exige. */
  const [editandoGasto, setEditandoGasto] = useState<GastoPropiedad | null>(null);
  const [documentos, setDocumentos] = useState<DocumentoPropiedad[] | null>(null);
  const [filtroInc, setFiltroInc] = useState<'ABIERTAS' | 'CERRADAS' | 'TODAS'>('ABIERTAS');
  const [filtroNivel, setFiltroNivel] = useState<number | null>(null);
  /*
    BUSCAR DENTRO DE LAS INCIDENCIAS.

    Una casa con ciento cincuenta incidencias abiertas no se recorre a ojo:
    quien busca «piscina» quiere las de la piscina, y hasta ahora la unica
    manera era bajar leyendo. Busca en el titulo, en la descripcion y en el
    codigo, sin tildes y sin mayusculas, porque nadie escribe «climatizacion»
    con tilde en un buscador.
  */
  const [busquedaInc, setBusquedaInc] = useState('');
  const [filtroHist, setFiltroHist] = useState<string>('TODOS');
  const [modal, setModal] = useState<
    null | 'mantenimiento' | 'proveedor' | 'documento' | 'visita' | 'iniciarVisita' | 'incidencia'
  >(null);
  const [ejecuciones, setEjecuciones] = useState<EjecucionVisita[] | null>(null);
  const [plantillas, setPlantillas] = useState<PlantillaResumen[]>([]);
  const [facturas, setFacturas] = useState<Factura[] | null>(null);

  const esDireccion = usuario.rol === 'DIRECTOR';
  const esInterno = esDireccion || usuario.rol === 'MANAGER';

  /*
    QUITAR PREGUNTA ANTES. El gasto deja de contar en el mes y en lo que se le
    factura al cliente; lo borrado queda entero en la auditoria del servidor. Un
    gasto que ya esta en una factura el servidor no deja tocarlo, y lo dice.
  */
  /*
    MARCAR COMO REPERCUTIBLE, desde la propia fila. La diferencia entre lo
    gastado y lo por cobrar de una casa es, casi siempre, gastos que se
    cargaron sin marcar «se le repercute al cliente». Arreglarlo no puede
    exigir abrir cada gasto: se pregunta y se marca. Lo repercutido es visible
    para el cliente, como al darlo de alta.
  */
  async function repercutir(g: GastoPropiedad) {
    if (!confirm(t(idioma, 'ficha.confirmarRepercutir', { concepto: g.concepto }))) return;
    try {
      await api.finEditarGasto(g.id, { facturableCliente: true, visibleCliente: true });
      setGastos(null);
    } catch (e) {
      setError(e instanceof ErrorApi && e.message ? e.message : t(idioma, 'carga.noSeGuardo'));
    }
  }

  async function quitarGasto(g: GastoPropiedad) {
    if (!confirm(t(idioma, 'carga.confirmarGasto', { concepto: g.concepto }))) return;
    try {
      await api.finEliminarGasto(g.id);
      setGastos(null);
    } catch (e) {
      setError(e instanceof ErrorApi && e.message ? e.message : t(idioma, 'carga.noSeQuito'));
    }
  }

  const cargar = useCallback(async () => {
    // Un 403 o un 404 en un bloque concreto no es un fallo de la pantalla: es la
    // respuesta correcta para ese rol. Se traduce en "aquí no hay nada que ver".
    const opcional = async <T,>(promesa: Promise<T>, porDefecto: T): Promise<T> => {
      try {
        return await promesa;
      } catch (fallo: unknown) {
        if (fallo instanceof ErrorApi && (fallo.estado === 403 || fallo.estado === 404)) {
          return porDefecto;
        }
        throw fallo;
      }
    };

    const [p, res, req, rel, con, inc, hist, par, ev, tab, usu, est, fot, tar] = await Promise.all([
      api.propiedad(propiedadId),
      opcional<{ resumen: ResumenFicha | null }>(api.resumenFicha(propiedadId), { resumen: null }),
      esInterno
        ? opcional<{ requisitos: RequisitosActivacion | null }>(api.requisitos(propiedadId), {
            requisitos: null,
          })
        : { requisitos: null },
      opcional(api.relaciones(propiedadId), { relaciones: [] }),
      esDireccion ? opcional(api.contratos(propiedadId), { contratos: [] }) : { contratos: [] },
      opcional(api.incidencias(propiedadId), { incidencias: [] }),
      opcional(api.historial(propiedadId), { eventos: [] }),
      esDireccion ? opcional(api.partes(), { partes: [] }) : { partes: [] },
      esDireccion
        ? opcional(api.evaluaciones(propiedadId), { evaluaciones: [] })
        : { evaluaciones: [] },
      esDireccion ? opcional(api.tablasNio(), { tablas: [] }) : { tablas: [] },
      esDireccion ? opcional(api.usuarios(), { usuarios: [] }) : { usuarios: [] },
      opcional(api.estancias(propiedadId), { estancias: [] }),
      opcional(api.fotos(propiedadId), { fotos: [] }),
      esDireccion ? opcional(api.tarifasEquipo(propiedadId), { tarifas: [] }) : { tarifas: [] },
    ]);

    setPropiedad(p.propiedad);
    setResumen(res.resumen);
    setRequisitos(req.requisitos);
    setRelaciones(rel.relaciones);
    setContratos(con.contratos);
    setIncidencias(inc.incidencias);
    setHistorial(hist.eventos);
    setPartes(par.partes);
    setEvaluaciones(ev.evaluaciones);
    setTabla(tab.tablas[0] ?? null);
    setUsuarios(usu.usuarios);
    setEstancias(est.estancias);
    setFotos(fot.fotos);
    setTarifas(tar.tarifas);
    setResponsable(p.propiedad.responsableInternoUserId ?? '');
  }, [propiedadId, esDireccion]);

  /**
   * LA AGENDA HA CAMBIADO.
   *
   * Las dos listas de la pestana se vuelven a pedir, y con ellas el resumen de
   * arriba: sus tres numeros —proxima, ultima y vencidas— se calculan sobre
   * las mismas citas. Se pide solo el resumen y no la ficha entera, que son
   * quince consultas para enterarse de una fecha.
   */
  const refrescarVisitas = useCallback(() => {
    setVisitas(null);
    setEjecuciones(null);
    void api
      .resumenFicha(propiedadId)
      .then((r) => setResumen(r.resumen))
      .catch(() => undefined);
  }, [propiedadId]);

  useEffect(() => {
    setCargando(true);
    cargar()
      .catch(() => setError(t(idioma, 'error.servidor')))
      .finally(() => setCargando(false));
  }, [cargar, idioma]);

  // Carga perezosa: cada pestaña pide lo suyo la primera vez que se abre.
  useEffect(() => {
    const fallo = () => setError(t(idioma, 'error.servidor'));
    if (pestana === 'visitas' && visitas === null) {
      api
        .visitasDePropiedad(propiedadId)
        .then((r) => setVisitas(r.visitas))
        .catch(fallo);
    }
    if (pestana === 'mantenimiento' && mantenimiento === null) {
      api
        .mantenimiento(propiedadId)
        .then((r) => setMantenimiento(r.mantenimiento))
        .catch(fallo);
    }
    if (directorio.length === 0) {
      api
        .proveedores()
        .then((r) => setDirectorio(r.proveedores))
        .catch(() => undefined);
    }
    if (pestana === 'proveedores' && proveedores === null) {
      api
        .proveedoresDePropiedad(propiedadId)
        .then((r) => setProveedores(r.proveedores))
        .catch(fallo);
      api
        .proveedores()
        .then((r) => setDirectorio(r.proveedores))
        .catch(() => undefined);
    }
    if (pestana === 'visitas' && ejecuciones === null) {
      api
        .ejecuciones(propiedadId)
        .then((r) => setEjecuciones(r.ejecuciones))
        .catch(fallo);
      api
        .plantillas()
        .then((r) => setPlantillas(r.plantillas))
        .catch(() => undefined);
    }
    if (pestana === 'gastos' && gastos === null) {
      api
        .gastosDePropiedad(propiedadId)
        .then((r) => setGastos(r))
        .catch(fallo);
    }
    if (pestana === 'contratos' && documentos === null) {
      api
        .documentos(propiedadId)
        .then((r) => setDocumentos(r.documentos))
        .catch(fallo);
    }
  }, [
    pestana,
    propiedadId,
    visitas,
    mantenimiento,
    proveedores,
    gastos,
    documentos,
    ejecuciones,
    directorio.length,
    idioma,
  ]);

  const recargar = () => {
    void cargar().catch(() => setError(t(idioma, 'error.servidor')));
  };

  const activar = () => {
    setError(null);
    api
      .cambiarEstado(propiedadId, 'ACTIVE')
      .then(recargar)
      .catch((fallo: unknown) =>
        setError(
          fallo instanceof ErrorApi ? t(idioma, fallo.mensajeClave) : t(idioma, 'error.servidor'),
        ),
      );
  };

  const dinero = (c: number) =>
    formatearImporteDesdeCents(c, idioma, { moneda: resumen?.moneda ?? 'EUR' });

  if (cargando) return <p>{t(idioma, 'app.cargando')}</p>;
  if (!propiedad)
    return (
      <p className="error" role="alert">
        {error ?? t(idioma, 'error.noEncontrado')}
      </p>
    );

  const puedeActivar =
    esDireccion &&
    requisitos?.puedeActivarse === true &&
    (propiedad.estado === 'ONBOARDING' ||
      propiedad.estado === 'SUSPENDED' ||
      propiedad.estado === 'INTERNAL_DEMO');

  const abiertas = incidencias.filter((i) => incidenciaActiva(i.estado)).length;
  const portada = fotos.find((f) => f.portada) ?? fotos[0];

  /*
    RECORRER UNA LISTA ES UNA PANTALLA, NO UN TROZO DE LA FICHA.

    La lista se dibujaba debajo de la pestana «Visitas», con todos sus puntos
    desplegados. Dos visitas a medias y la pestana eran cuarenta lineas de
    checklist por las que bajar antes de ver que habia programado para el jueves.

    Y, lo que es peor, se podia salir de ella sin querer: bastaba con pulsar
    otra pestana. Aqui no: desde dentro de una visita se sale cerrandola —y
    para eso hacen falta sus fotos— o descartandola a proposito.
  */
  const visitaEnPantalla =
    abierta === null ? undefined : (ejecuciones ?? []).find((e) => e.id === abierta);

  if (visitaEnPantalla) {
    return (
      <main>
        <PanelEjecucion
          idioma={idioma}
          ejecucion={visitaEnPantalla}
          casa={propiedad.nombre}
          onSalir={() => {
            /*
              Las listas de la ficha se vuelven a pedir AL SALIR, no al cerrar
              la visita. Pidiendolas antes, la pantalla se quedaba un instante
              sin la visita que estaba enseñando —se habia vaciado la lista de
              la que sale— y parpadeaba a la ficha y de vuelta.
            */
            setAbierta(null);
            refrescarVisitas();
          }}
          onCambio={() => {
            // La cita de la agenda cambia: pasa a «hecha» o vuelve a pendiente.
            setVisitas(null);
          }}
        />
      </main>
    );
  }

  return (
    <main>
      <div className="portada-villa">
        {portada ? (
          <img
            src={portada.url}
            alt={propiedad.nombre}
            style={{ width: '100%', height: 280, objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <FotoVilla semilla={semillaDe(propiedad.id)} alto={280} etiqueta variante="ancha" />
        )}

        <div className="encima-derecha">
          <span className="sobre-foto">
            <i style={{ background: COLOR_SEMAFORO[propiedad.semaforo] }} />
            {t(idioma, `semaforo.${propiedad.semaforo}`)}
          </span>
          <ChipNio nivel={propiedad.nivelNio} sobreFoto />
        </div>

        {/*
          «Volver» es hermano de la banda de abajo, no hijo suyo. Dentro, su
          posicion se medía contra la banda —que ya esta anclada al pie— y
          terminaba en mitad de la foto, encima del nombre de la casa. Fuera, se
          coloca contra la portada: abajo a la derecha en el escritorio y arriba
          a la izquierda en el movil, que es donde una app pone el atras.
        */}
        <div className="fila-flex acciones-portada">
          <button type="button" className="boton secundario pequeno" onClick={onVolver}>
            <Icono nombre="volver" /> {t(idioma, 'app.volver')}
          </button>
        </div>

        <div className="encima-abajo">
          <div style={{ minWidth: 0 }}>
            <span className="chapa-villa">
              <Icono nombre="casa" /> {propiedad.codigo}
            </span>
            <h1>{propiedad.nombre}</h1>
            <p className="sitio">
              <Icono nombre="pin" /> {propiedad.zona ?? propiedad.pais} ·{' '}
              {t(idioma, `estadoPropiedad.${propiedad.estado}`)}
            </p>
          </div>
        </div>
      </div>

      <div className="pestanas" role="tablist" aria-label={propiedad.nombre}>
        {PESTANAS.map((clave) => (
          <button
            key={clave}
            type="button"
            role="tab"
            aria-selected={pestana === clave}
            onClick={() => setPestana(clave)}
          >
            {t(idioma, `ficha.${clave}`)}
            {clave === 'incidencias' && abiertas > 0 && (
              <span className="punto-rojo" aria-hidden="true" />
            )}
          </button>
        ))}
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {propiedad.estado === 'INTERNAL_DEMO' && (
        <p className="aviso-demo">
          <strong>{t(idioma, 'estadoPropiedad.INTERNAL_DEMO')}</strong> —{' '}
          {t(idioma, 'propiedades.demoExcluida')}
        </p>
      )}

      {/* --- Resumen --------------------------------------------------------- */}
      {pestana === 'resumen' && (
        <>
          {resumen && (
            <div className="rejilla kpis">
              <div className="tarjeta kpi-card">
                <h3>{t(idioma, 'ficha.proximaVisita')}</h3>
                <span className="kpi-valor fecha">{fechaLarga(resumen.proximaVisita, idioma)}</span>
                {resumen.visitasVencidas > 0 && (
                  <span className="kpi-pie rojo">
                    {t(idioma, 'ficha.visitasVencidas', { total: resumen.visitasVencidas })}
                  </span>
                )}
              </div>
              <div className="tarjeta kpi-card">
                <h3>{t(idioma, 'ficha.ultimaVisita')}</h3>
                <span className="kpi-valor fecha">{fechaLarga(resumen.ultimaVisita, idioma)}</span>
                {!resumen.ultimaVisita && (
                  <span className="kpi-pie">{t(idioma, 'ficha.sinVisitas')}</span>
                )}
              </div>
              <div className="tarjeta kpi-card">
                <h3>{t(idioma, 'ficha.incidenciasActivas')}</h3>
                <span className={`kpi-valor ${resumen.incidenciasAbiertas > 0 ? 'rojo' : ''}`}>
                  {resumen.incidenciasAbiertas}
                </span>
                <span className="kpi-pie">
                  {Object.entries(resumen.incidenciasPorNivel)
                    .map(([n, c]) => `${n} ${c}`)
                    .join(' · ') || '—'}
                </span>
              </div>
              <div className="tarjeta kpi-card">
                <h3>{t(idioma, 'ficha.gastoAcumulado')}</h3>
                <span className="kpi-valor">{dinero(resumen.gastoTotalCents)}</span>
                <span className="kpi-pie">
                  {t(idioma, 'ficha.horasImputadas', { total: resumen.horasTotales })}
                </span>
              </div>
            </div>
          )}

          {requisitos && propiedad.estado !== 'ACTIVE' && (
            <section className="tarjeta">
              <h2>{t(idioma, 'propiedades.requisitos')}</h2>
              {requisitos.pendientes.length === 0 ? (
                <p>{t(idioma, 'propiedades.sinRequisitos')}</p>
              ) : (
                <>
                  <p>{t(idioma, 'propiedades.impideActivacion')}</p>
                  <ul>
                    {requisitos.pendientes.map((codigo) => (
                      <li key={codigo}>{t(idioma, `requisito.${codigo}`)}</li>
                    ))}
                  </ul>
                </>
              )}
              {esDireccion && (
                <>
                  <div className="campo">
                    <label htmlFor={idResponsable}>
                      {t(idioma, 'requisito.RESPONSABLE_INTERNO')}
                    </label>
                    <select
                      id={idResponsable}
                      value={responsable}
                      onChange={(e) => {
                        setResponsable(e.target.value);
                        void api
                          .editarPropiedad(propiedadId, {
                            responsableInternoUserId: e.target.value || null,
                          })
                          .then(recargar);
                      }}
                    >
                      <option value="">—</option>
                      {usuarios.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button type="button" disabled={!puedeActivar} onClick={activar}>
                    {t(idioma, 'propiedades.activar')}
                  </button>
                </>
              )}
            </section>
          )}

          <Estancias
            idioma={idioma}
            propiedadId={propiedadId}
            estancias={estancias}
            puedeCrear={esInterno}
            puedeAnular={esDireccion}
            onCambio={recargar}
          />

          <Fotos
            idioma={idioma}
            propiedadId={propiedadId}
            fotos={fotos}
            puedeGestionar={esInterno}
            onCambio={recargar}
          />

          {esDireccion && tarifas.length > 0 && (
            <TarifasEquipoTabla
              idioma={idioma}
              propiedadId={propiedadId}
              tarifas={tarifas}
              onCambio={recargar}
            />
          )}

          <PanelRelaciones
            propiedadId={propiedadId}
            relaciones={relaciones}
            partes={partes}
            puedeGestionar={esDireccion}
            idioma={idioma}
            onCambio={recargar}
          />

          {/*
            LA EVALUACION OPERATIVA ES DE DIRECCION.

            Fija el nivel NIO, y de el sale lo que se le cobra al cliente. Un
            manager no decide eso, asi que ensenarsela solo servia para que
            pareciera que si. Antes se pintaba para todo el equipo interno.
          */}
          {esDireccion && (
            <PanelNio
              propiedadId={propiedadId}
              evaluaciones={evaluaciones}
              tabla={tabla}
              esDireccion={esDireccion}
              idioma={idioma}
              onCambio={recargar}
            />
          )}

          {esDireccion && (
            <PanelCicloVida
              propiedadId={propiedadId}
              estado={propiedad.estado}
              requisitos={requisitos}
              incidenciasAbiertas={abiertas}
              contratoVigente={contratos.some((c) => c.estado === 'ACTIVE')}
              idioma={idioma}
              onCambio={recargar}
              onError={setError}
            />
          )}
        </>
      )}

      {/* --- Incidencias ----------------------------------------------------- */}
      {pestana === 'incidencias' && (
        <>
          <div className="fila-filtros">
            <div
              className="conmutador-vista"
              role="group"
              aria-label={t(idioma, 'ficha.incidencias')}
            >
              {(['ABIERTAS', 'CERRADAS', 'TODAS'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  aria-pressed={filtroInc === f}
                  onClick={() => setFiltroInc(f)}
                >
                  {t(idioma, `ficha.filtro${f}`)}
                </button>
              ))}
            </div>
            <div className="conmutador-vista" role="group" aria-label={t(idioma, 'ficha.nivel')}>
              <button
                type="button"
                aria-pressed={filtroNivel === null}
                onClick={() => setFiltroNivel(null)}
              >
                {t(idioma, 'ficha.todos')}
              </button>
              {[1, 2, 3, 4].map((n) => (
                <button
                  key={n}
                  type="button"
                  aria-pressed={filtroNivel === n}
                  onClick={() => setFiltroNivel(n)}
                >
                  N{n}
                </button>
              ))}
            </div>
            <label className="campo campo-buscar">
              <span className="oculto-visual">{t(idioma, 'ficha.buscarIncidencia')}</span>
              <input
                type="search"
                value={busquedaInc}
                placeholder={t(idioma, 'ficha.buscarIncidencia')}
                onChange={(e) => setBusquedaInc(e.target.value)}
              />
            </label>
            {esInterno && (
              <button
                type="button"
                className="alta-incidencia"
                onClick={() => setModal('incidencia')}
              >
                + {t(idioma, 'incidencia.agregar')}
              </button>
            )}
          </div>
          <ListaIncidencias
            idioma={idioma}
            incidencias={incidencias.filter((i) => {
              if (filtroInc === 'ABIERTAS' && !incidenciaActiva(i.estado)) return false;
              if (filtroInc === 'CERRADAS' && incidenciaActiva(i.estado)) return false;
              if (filtroNivel !== null && i.nivel !== filtroNivel) return false;
              if (!contiene([i.titulo, i.descripcion, i.codigo], busquedaInc)) return false;
              return true;
            })}
            dinero={dinero}
            puedeGestionar={esInterno}
            puedeAnular={esDireccion}
            onAbrir={onAbrirIncidencia}
            onCambio={recargar}
          />
        </>
      )}

      {/* --- Visitas --------------------------------------------------------- */}
      {pestana === 'visitas' && (
        <section className="tarjeta">
          <div className="tarjeta-cabeza">
            <h2>{t(idioma, 'ficha.visitas')}</h2>
            {esInterno && (
              <span className="fila-flex" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                <button type="button" onClick={() => setModal('iniciarVisita')}>
                  <Icono nombre="reproducir" /> {t(idioma, 'visita.iniciar')}
                </button>
                <button type="button" className="secundario" onClick={() => setModal('visita')}>
                  <Icono nombre="calendario" /> {t(idioma, 'visita.programar')}
                </button>
              </span>
            )}
          </div>
          {visitas === null && <p>{t(idioma, 'app.cargando')}</p>}
          {visitas !== null && visitas.length === 0 && (
            <p className="suave">{t(idioma, 'ficha.sinVisitas')}</p>
          )}
          {visitas !== null && visitas.length > 0 && (
            <div className="lista">
              {visitas.map((v) => {
                /*
                  Una visita programada puede tener ya su lista empezada. Si la
                  tiene, se continua o se mira; si no, se empieza. Lo que NO se
                  hace nunca es empezar una segunda encima de la misma.
                */
                const ejecucion = (ejecuciones ?? []).find((e) => e.eventoId === v.id);
                const sePuedeRecorrer = esInterno && v.estado !== 'CANCELADA';
                const abrirLista = () => {
                  if (!sePuedeRecorrer) return;
                  if (ejecucion) {
                    setAbierta(ejecucion.id);
                    return;
                  }
                  /*
                    LA CITA YA SABE QUE LISTA SE RECORRE.

                    Si se programo eligiendo plantilla, preguntar otra vez cual
                    es seria preguntar lo que uno mismo contesto la semana
                    pasada. Se empieza y punto. Solo se abre el dialogo con las
                    citas antiguas, que se programaron sin lista.
                  */
                  if (v.plantillaId) {
                    void api
                      .iniciarVisita(propiedadId, {
                        plantillaId: v.plantillaId,
                        eventoId: v.id,
                      })
                      .then((r) => {
                        setAbierta(r.ejecucion.id);
                        refrescarVisitas();
                      })
                      .catch(() =>
                        setVisitaDeAgenda({
                          id: v.id,
                          titulo: v.titulo,
                          // Sin lista, el motivo es su titulo y su nota: las dos cosas.
                          motivo: v.plantillaId
                            ? null
                            : [v.titulo, v.notas].filter(Boolean).join('\n'),
                        }),
                      );
                    return;
                  }
                  setVisitaDeAgenda({
                    id: v.id,
                    titulo: v.titulo,
                    // Sin lista, el motivo es su titulo y su nota: las dos cosas.
                    motivo: v.plantillaId ? null : [v.titulo, v.notas].filter(Boolean).join('\n'),
                  });
                };
                /*
                  «Continuar la lista» en una visita que no tiene lista manda a
                  buscar algo que no existe. Cuando se fue por un motivo y no a
                  recorrer puntos, se dice en esos terminos.
                */
                const hayLista = (ejecucion?.items.length ?? 0) > 0;
                const queHacer = ejecucion
                  ? ejecucion.estado === 'EN_CURSO'
                    ? hayLista
                      ? 'visita.continuarLista'
                      : 'visita.continuarVisita'
                    : hayLista
                      ? 'visita.verLista'
                      : 'visita.verVisita'
                  : 'visita.iniciar';
                return (
                  <div
                    key={v.id}
                    className={`fila${sePuedeRecorrer ? ' fila-pulsable' : ''}`}
                    {...(sePuedeRecorrer
                      ? {
                          role: 'button' as const,
                          tabIndex: 0,
                          'aria-label': `${t(idioma, queHacer)}: ${v.titulo}`,
                          onClick: abrirLista,
                          onKeyDown: (e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              abrirLista();
                            }
                          },
                        }
                      : {})}
                  >
                    <span className="pastilla info">{t(idioma, `evento.${v.tipo}`)}</span>
                    <span className="crecer">
                      <b>{v.titulo}</b>
                      <small>
                        {fechaLarga(v.fecha, idioma)}
                        {v.hora ? ` · ${v.hora}` : ''}
                        {v.asignadoNombre ? ` · ${v.asignadoNombre}` : ''}
                        {ejecucion
                          ? ` · ${t(idioma, `visita.estado${ejecucion.estado}`)}${
                              ejecucion.items.length > 0
                                ? ` · ${t(idioma, 'visita.resumenPlegado', {
                                    hechos: ejecucion.items.filter(
                                      (i) => i.resultado !== 'PENDIENTE',
                                    ).length,
                                    total: ejecucion.items.length,
                                    fotos: ejecucion.items.reduce((n, i) => n + i.fotos.length, 0),
                                  })}`
                                : ''
                            }`
                          : ''}
                      </small>
                    </span>
                    {/* Lo que se viene a hacer aqui, escrito y no adivinado. */}
                    {sePuedeRecorrer && (
                      <button
                        type="button"
                        className={ejecucion ? 'secundario' : ''}
                        onClick={(e) => {
                          e.stopPropagation();
                          abrirLista();
                        }}
                      >
                        <Icono nombre={ejecucion ? 'checklist' : 'reproducir'} />{' '}
                        {t(idioma, queHacer)}
                      </button>
                    )}
                    {/*
                    Abierta → En curso → Cerrada. Se elige con un desplegable y
                    no con tres botones porque una visita cambia de estado una o
                    dos veces en su vida, no a cada rato.
                  */}
                    {esInterno ? (
                      <select
                        aria-label={t(idioma, 'ficha.visitas')}
                        onClick={(e) => e.stopPropagation()}
                        value={v.estado}
                        className={`estado-visita ${
                          v.estado === 'HECHA'
                            ? 'verde'
                            : v.estado === 'CANCELADA'
                              ? 'neutra'
                              : 'ambar'
                        }`}
                        onChange={(ev) => {
                          void api
                            .cambiarEstadoEvento(v.id, ev.target.value as EstadoEvento)
                            .then(() => refrescarVisitas())
                            .catch(() => undefined);
                        }}
                      >
                        {ESTADOS_VISITA.map((e) => (
                          <option key={e} value={e}>
                            {t(idioma, `estadoEvento.${e}`)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span
                        className={`pastilla ${
                          v.estado === 'HECHA'
                            ? 'verde'
                            : v.estado === 'CANCELADA'
                              ? 'neutra'
                              : 'ambar'
                        }`}
                      >
                        {t(idioma, `estadoEvento.${v.estado}`)}
                      </span>
                    )}
                    {/*
                    EL LAPIZ Y LA PAPELERA, como en Interno. Lo que mas cambia
                    de una visita es la fecha —llueve, el cliente se retrasa— y
                    hasta ahora eso obligaba a borrarla y crearla otra vez, con
                    lo que la lista ya empezada se quedaba huerfana.

                    Quitar sigue siendo para la visita que no debio existir. La
                    que de verdad se cancelo se marca «Cancelada» y se queda:
                    borrarla haria desaparecer algo que si paso.
                  */}
                    {esInterno && (
                      <AccionesCarga
                        idioma={idioma}
                        onEditar={() => setEditandoVisita(v)}
                        onEliminar={() => {
                          if (
                            !window.confirm(
                              t(idioma, 'visita.confirmarQuitar', { titulo: v.titulo }),
                            )
                          ) {
                            return;
                          }
                          void api
                            .eliminarEvento(v.id)
                            .then(() => refrescarVisitas())
                            .catch((f: unknown) =>
                              setError(
                                f instanceof ErrorApi
                                  ? t(idioma, f.mensajeClave)
                                  : t(idioma, 'error.servidor'),
                              ),
                            );
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/*
        LAS VISITAS SIN CITA.

        Las que salieron de la agenda ya tienen su fila arriba, con su fecha y
        su estado: repetirlas aqui era ensenar dos veces la misma visita sin
        manera de saber cual de las dos era. Aqui quedan las que se empezaron
        sobre la marcha, y plegadas: lo que se consulta de una visita es
        puntual —que decia aquel punto, que foto se hizo— asi que se abre
        cuando se pide.
      */}
      {pestana === 'visitas' &&
        (ejecuciones ?? []).filter((e) => e.eventoId === null).length > 0 && (
          <section className="tarjeta">
            <div className="tarjeta-cabeza">
              <h2>{t(idioma, 'visita.realizadas')}</h2>
            </div>
            <div className="lista">
              {(ejecuciones ?? [])
                .filter((e) => e.eventoId === null)
                .map((e) => {
                  const hechos = e.items.filter((i) => i.resultado !== 'PENDIENTE').length;
                  const fotos = e.items.reduce((n, i) => n + i.fotos.length, 0);
                  const enCurso = e.estado === 'EN_CURSO';
                  return (
                    <button
                      key={e.id}
                      type="button"
                      className="fila fila-pulsable"
                      onClick={() => setAbierta(e.id)}
                    >
                      <span className="crecer">
                        {/*
                          La visita sin lista se titula con su motivo. Antes
                          ponia «Sin lista» en las tres de la semana y habia que
                          abrirlas una a una para saber cual era cual.
                        */}
                        <b>
                          {e.plantillaNombre ??
                            e.notas?.split('\n')[0]?.slice(0, 80) ??
                            t(idioma, 'visita.sinPlantilla')}
                        </b>
                        <small className="suave">
                          {fechaLarga(e.completadaEn ?? e.iniciadaEn, idioma)}
                          {e.ejecutorNombre ? ` · ${e.ejecutorNombre}` : ''}
                          {e.items.length > 0
                            ? ` · ${t(idioma, 'visita.resumenPlegado', {
                                hechos,
                                total: e.items.length,
                                fotos,
                              })}`
                            : ''}
                        </small>
                      </span>
                      <span className={`pastilla ${enCurso ? 'ambar' : 'verde'}`}>
                        {t(idioma, `visita.estado${e.estado}`)}
                      </span>
                      <Icono nombre="checklist" />
                    </button>
                  );
                })}
            </div>
          </section>
        )}

      {/* --- Mantenimiento --------------------------------------------------- */}
      {pestana === 'mantenimiento' && (
        <PanelMantenimiento
          idioma={idioma}
          propiedadId={propiedadId}
          tareas={mantenimiento}
          puedeGestionar={esInterno}
          onCambio={() => setMantenimiento(null)}
          onNueva={() => setModal('mantenimiento')}
        />
      )}

      {/* --- Proveedores ----------------------------------------------------- */}
      {pestana === 'proveedores' && (
        <PanelProveedoresPropiedad
          idioma={idioma}
          propiedadId={propiedadId}
          vinculados={proveedores}
          directorio={directorio}
          puedeGestionar={esInterno}
          onCambio={() => setProveedores(null)}
          onNuevo={() => setModal('proveedor')}
        />
      )}

      {/* --- Gastos ---------------------------------------------------------- */}
      {pestana === 'gastos' && facturas !== null && (
        <section className="tarjeta">
          <div className="tarjeta-cabeza">
            <h2>{t(idioma, 'factura.titulo')}</h2>
            {esDireccion && (
              <button
                type="button"
                onClick={() => {
                  void api
                    .facturarPendiente(propiedadId)
                    .then(() => {
                      setFacturas(null);
                      setGastos(null);
                    })
                    .catch((f: unknown) =>
                      setError(
                        f instanceof ErrorApi
                          ? t(idioma, f.mensajeClave)
                          : t(idioma, 'error.servidor'),
                      ),
                    );
                }}
              >
                + {t(idioma, 'factura.crear')}
              </button>
            )}
          </div>
          {facturas.length === 0 ? (
            <p className="suave">{t(idioma, 'factura.sinFacturas')}</p>
          ) : (
            <div className="lista">
              {facturas.map((f) => (
                <div key={f.id} className="fila">
                  <Icono nombre="factura" />
                  <span className="crecer">
                    <b>{f.numero}</b>
                    <small>
                      {t(idioma, `factura.estado${f.estado}`)}
                      {f.fechaEmision ? ` · ${f.fechaEmision}` : ''}
                      {f.diasVencida !== null && f.diasVencida > 0
                        ? ` · ${t(idioma, 'factura.vencidaDias', { total: f.diasVencida })}`
                        : ''}
                    </small>
                  </span>
                  <span className="numerico">{dinero(f.totalCents)}</span>
                  {esDireccion && f.estado === 'BORRADOR' && (
                    <button
                      type="button"
                      className="enlace"
                      onClick={() => void api.emitirFactura(f.id).then(() => setFacturas(null))}
                    >
                      {t(idioma, 'factura.emitir')}
                    </button>
                  )}
                  {esDireccion && f.estado === 'EMITIDA' && (
                    <button
                      type="button"
                      className="enlace"
                      onClick={() => void api.cobrarFactura(f.id).then(() => setFacturas(null))}
                    >
                      {t(idioma, 'factura.cobrar')}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {pestana === 'gastos' && (
        <section className="tarjeta">
          <div className="tarjeta-cabeza">
            <h2>{t(idioma, 'ficha.gastos')}</h2>
            <span className="fila-flex" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
              {/*
                Ya no se anade un gasto desde aqui. Habia dos formularios
                distintos para lo mismo, con campos distintos, y uno de ellos se
                dejaba por el camino si el gasto se repercutia al cliente. Ahora
                el unico camino es «＋ Registrar», que pregunta lo necesario
                paso a paso y obliga a adjuntar el comprobante.
              */}
            </span>
            {gastos && (
              <span className="fila-flex" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                {/*
                  TRES NUMEROS Y CADA UNO CON SU NOMBRE. Antes salia solo lo
                  gastado, sin rotulo, y se leia como lo que el cliente debe: en
                  Casa Roger «4.607,08» contra los 1.050 del Panel. Ahora:
                  gastado, por cobrar (el mismo numero que el Panel, con el mismo
                  criterio) y lo que no se le repercute, que es la diferencia.
                */}
                <span className="suave">
                  {t(idioma, 'ficha.gastado')} <b>{dinero(gastos.totales.costeCents)}</b>
                </span>
                <span className="pastilla verde" title={t(idioma, 'ficha.porCobrarAyuda')}>
                  {t(idioma, 'ficha.porCobrar')} {dinero(gastos.totales.porCobrarCents)}
                </span>
                {gastos.totales.noRepercutidoGastos > 0 && (
                  <span className="pastilla neutra" title={t(idioma, 'ficha.noRepercutidoAyuda')}>
                    {t(idioma, 'ficha.noRepercutido', {
                      importe: dinero(gastos.totales.noRepercutidoCents),
                      total: gastos.totales.noRepercutidoGastos,
                    })}
                  </span>
                )}
                {gastos.totales.sinContabilizar > 0 && (
                  <span className="pastilla ambar">
                    {gastos.totales.sinContabilizar} {t(idioma, 'interno.contab')}
                  </span>
                )}
              </span>
            )}
          </div>
          {gastos === null && <p>{t(idioma, 'app.cargando')}</p>}
          {gastos !== null && gastos.gastos.length === 0 && (
            <p className="suave">{t(idioma, 'interno.vacio')}</p>
          )}
          {gastos !== null && gastos.gastos.length > 0 && (
            <div className="tabla-scroll">
              <table>
                <thead>
                  <tr>
                    <th scope="col" className="col-fecha">
                      {t(idioma, 'interno.fecha')}
                    </th>
                    <th scope="col">{t(idioma, 'interno.concepto')}</th>
                    <th scope="col" className="col-descripcion">
                      {t(idioma, 'gastos.descripcion')}
                    </th>
                    <th scope="col">{t(idioma, 'interno.beneficiario')}</th>
                    <th scope="col" className="col-persona">
                      {t(idioma, 'interno.persona')}
                    </th>
                    <th scope="col">{t(idioma, 'interno.categoria')}</th>
                    <th scope="col">{t(idioma, 'interno.estado')}</th>
                    <th scope="col">{t(idioma, 'cobro.titulo')}</th>
                    <th scope="col" className="col-factura">
                      {t(idioma, 'gasto.factura')}
                    </th>
                    <th scope="col" className="num">
                      {t(idioma, 'interno.importe')}
                    </th>
                    <th scope="col" className="num">
                      {t(idioma, 'ficha.precioCliente')}
                    </th>
                    {esDireccion && (
                      <th scope="col" className="col-acciones">
                        <span className="solo-lectores">{t(idioma, 'carga.acciones')}</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {gastos.gastos.map((g) => (
                    <tr
                      key={g.id}
                      className="fila-pulsable"
                      tabIndex={0}
                      role="button"
                      aria-label={t(idioma, 'gasto.verFactura')}
                      onClick={() => setFactura(g)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setFactura(g);
                        }
                      }}
                    >
                      <td className="numerico col-fecha">{fechaLarga(g.fecha, idioma)}</td>
                      <td>{g.concepto}</td>
                      <td className="col-descripcion">
                        {/*
                          QUE SE HIZO: las incidencias a las que responde y lo
                          escrito a mano. Se completa luego desde «Editar».
                        */}
                        {g.incidencias.length === 0 && !g.descripcion ? (
                          <span className="suave">—</span>
                        ) : (
                          <span className="descripcion-gasto">
                            {g.incidencias.map((i) => (
                              <span key={i.id} className="descripcion-incidencia">
                                <b>{i.codigo}</b> – {i.titulo}
                              </span>
                            ))}
                            {g.descripcion && <span>{g.descripcion}</span>}
                          </span>
                        )}
                      </td>
                      <td>{g.beneficiario ?? <span className="suave">—</span>}</td>
                      <td className="col-persona">
                        {g.autorNombre ?? <span className="suave">—</span>}
                      </td>
                      <td>{t(idioma, `categoriaGasto.${g.categoria}`)}</td>
                      <td>
                        <span className={`pastilla ${CLASE_ESTADO_GASTO[g.estado] ?? 'neutra'}`}>
                          {t(idioma, `estadoGasto.${g.estado}`)}
                        </span>
                      </td>
                      {/* El selector de cobro se maneja solo: pulsarlo no abre la factura. */}
                      <td onClick={(e) => e.stopPropagation()}>
                        {/*
                          «POR COBRAR» SOLO SI SE LE COBRA. Un gasto que no se le
                          repercute al cliente salia igualmente «Por cobrar», y
                          eso sumaba en la cabeza de quien lo leia lo que no iba a
                          sumar en ninguna factura. Ahora dice que no se repercute,
                          y Direccion puede cambiarlo ahi mismo.
                        */}
                        {!g.facturableCliente && g.horas ? (
                          // Horas dentro del fee: se cambia desde «Editar», no aqui.
                          <span className="pastilla neutra">
                            {t(idioma, 'gastos.incluidoEnFee')}
                          </span>
                        ) : !g.facturableCliente ? (
                          esDireccion ? (
                            <button
                              type="button"
                              className="enlace pequeno"
                              title={t(idioma, 'ficha.repercutirAyuda')}
                              onClick={() => void repercutir(g)}
                            >
                              {t(idioma, 'ficha.repercutir')}
                            </button>
                          ) : (
                            <span className="pastilla neutra">
                              {t(idioma, 'ficha.noSeRepercute')}
                            </span>
                          )
                        ) : esInterno ? (
                          <Cobro idioma={idioma} gasto={g} onCambio={() => setGastos(null)} />
                        ) : (
                          <span className={`pastilla ${CLASE_COBRO[g.estadoCobro]}`}>
                            {t(idioma, `cobro.${g.estadoCobro}`)}
                          </span>
                        )}
                      </td>
                      <td className="col-factura">
                        <SenalFactura idioma={idioma} adjuntos={g.adjuntos} />
                      </td>
                      <td className="num numerico">{dinero(g.costeCents)}</td>
                      <td className="num numerico">
                        {g.facturableCliente ? (
                          <>
                            {dinero(g.precioClienteCents)}
                            {g.margenPb > 0 && !g.horas && (
                              <span className="suave"> +{g.margenPb / 100}%</span>
                            )}
                          </>
                        ) : g.horas ? (
                          <span className="suave">{dinero(0)}</span>
                        ) : (
                          <span className="suave">—</span>
                        )}
                      </td>
                      {esDireccion && (
                        <td className="col-acciones">
                          <AccionesCarga
                            idioma={idioma}
                            onEditar={() => setEditandoGasto(g)}
                            onEliminar={() => void quitarGasto(g)}
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* --- Contratos ------------------------------------------------------- */}
      {pestana === 'contratos' && (
        <>
          <PanelDocumentos
            idioma={idioma}
            propiedadId={propiedadId}
            documentos={documentos}
            puedeGestionar={esInterno}
            onCambio={() => setDocumentos(null)}
            onSubir={() => setModal('documento')}
          />
          {esDireccion && (
            <PanelContrato
              propiedadId={propiedadId}
              contratos={contratos}
              partes={partes}
              evaluaciones={evaluaciones}
              idioma={idioma}
              onCambio={recargar}
            />
          )}
        </>
      )}

      {/* --- Historial ------------------------------------------------------- */}
      {pestana === 'historial' && (
        <section className="tarjeta">
          <div className="tarjeta-cabeza">
            <h2>{t(idioma, 'propiedades.historial')}</h2>
            <span className="suave">{historial.length}</span>
          </div>
          <div className="fila-filtros">
            {['TODOS', ...new Set(historial.map((e) => e.tipo))].map((tp) => (
              <button
                key={tp}
                type="button"
                className={filtroHist === tp ? 'chip-sla' : 'chip-area'}
                onClick={() => setFiltroHist(tp)}
              >
                {tp === 'TODOS' ? t(idioma, 'ficha.todos') : tp.replace(/_/g, ' ').toLowerCase()}
              </button>
            ))}
          </div>
          {historial.length === 0 ? (
            <p className="suave">—</p>
          ) : (
            <div className="lista">
              {historial
                .filter((e) => filtroHist === 'TODOS' || e.tipo === filtroHist)
                .map((evento) => (
                  <div key={evento.id} className="fila">
                    <span className="crecer">
                      <b>{evento.titulo}</b>
                      {evento.detalle && <small>{evento.detalle}</small>}
                    </span>
                    <span className="pequeno suave numerico">
                      {formatearFechaHora(new Date(evento.ocurridoEn), idioma)}
                    </span>
                  </div>
                ))}
            </div>
          )}
        </section>
      )}

      {modal === 'mantenimiento' && (
        <ModalMantenimiento
          idioma={idioma}
          propiedadId={propiedadId}
          proveedores={directorio}
          onHecho={() => setMantenimiento(null)}
          onCerrar={() => setModal(null)}
        />
      )}
      {modal === 'proveedor' && (
        <ModalProveedor
          idioma={idioma}
          propiedadId={propiedadId}
          onHecho={() => {
            setProveedores(null);
            setDirectorio([]);
          }}
          onCerrar={() => setModal(null)}
        />
      )}
      {modal === 'incidencia' && (
        <Modal
          idioma={idioma}
          titulo={t(idioma, 'incidencia.agregar')}
          icono="aviso"
          onCerrar={() => setModal(null)}
        >
          <NuevaIncidencia
            idioma={idioma}
            propiedadId={propiedadId}
            onCreada={() => {
              setModal(null);
              recargar();
            }}
            onCancelar={() => setModal(null)}
          />
        </Modal>
      )}

      {modal === 'documento' && (
        <ModalDocumento
          idioma={idioma}
          propiedadId={propiedadId}
          onHecho={() => setDocumentos(null)}
          onCerrar={() => setModal(null)}
        />
      )}
      {(modal === 'iniciarVisita' || visitaDeAgenda) && (
        <ModalIniciarVisita
          idioma={idioma}
          propiedadId={propiedadId}
          plantillas={plantillas}
          {...(visitaDeAgenda ? { evento: visitaDeAgenda } : {})}
          onHecho={(ejecucionId) => {
            // La agenda tambien cambia: la visita pasa a «en curso».
            refrescarVisitas();
            // Y se entra en ella: empezar una visita es ponerse a recorrerla.
            setAbierta(ejecucionId);
          }}
          onCerrar={() => {
            setModal(null);
            setVisitaDeAgenda(null);
          }}
        />
      )}
      {/* Cambiar la fecha, la hora, quien va o que lista se recorre. */}
      {editandoVisita && (
        <ModalEditarVisita
          idioma={idioma}
          evento={editandoVisita}
          usuarios={usuarios}
          plantillas={plantillas}
          onHecho={() => refrescarVisitas()}
          onCerrar={() => setEditandoVisita(null)}
        />
      )}
      {modal === 'visita' && (
        <ModalVisita
          idioma={idioma}
          propiedadId={propiedadId}
          usuarios={usuarios}
          plantillas={plantillas}
          onHecho={() => refrescarVisitas()}
          onCerrar={() => setModal(null)}
        />
      )}
      {editandoGasto && (
        <ModalEditarGasto
          idioma={idioma}
          gasto={editandoGasto}
          propiedadId={propiedadId}
          onGuardado={() => setGastos(null)}
          onCerrar={() => setEditandoGasto(null)}
        />
      )}

      {factura && (
        <FacturaDelGasto
          idioma={idioma}
          gastoId={factura.id}
          concepto={factura.concepto}
          adjuntos={factura.adjuntos}
          onSubida={(adjunto) => {
            // Se parchea la fila en vez de volver a pedir la lista entera: es
            // lo mismo que acaba de pasar, y ya se sabe.
            setGastos((antes) =>
              antes === null
                ? antes
                : {
                    ...antes,
                    gastos: antes.gastos.map((g) =>
                      g.id === factura.id ? { ...g, adjuntos: [...g.adjuntos, adjunto] } : g,
                    ),
                  },
            );
          }}
          onCerrar={() => setFactura(null)}
        />
      )}
    </main>
  );
}

// --- Estancias ---------------------------------------------------------------

function Estancias({
  idioma,
  propiedadId,
  estancias,
  puedeCrear,
  puedeAnular,
  onCambio,
}: {
  idioma: Idioma;
  propiedadId: string;
  estancias: Estancia[];
  puedeCrear: boolean;
  /** Quitar una estancia pasada es cosa de Direccion. */
  puedeAnular: boolean;
  onCambio: () => void;
}) {
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [huesped, setHuesped] = useState('');
  const [enviando, setEnviando] = useState(false);

  /*
    La lista crecia sin fin: cada llegada y cada salida de los ultimos anos, con
    las de hace tres veranos ocupando el mismo sitio que la de la semana que
    viene, hasta esconder la unica que importa —la que esta en curso—.

    Se ANULA, no se borra, y se pregunta antes: una estancia puede estar ya
    escrita en una factura, y equivocarse de fila no se deshace.
  */
  const anular = (es: Estancia) => {
    const cuando = `${fechaLarga(es.desde, idioma)} → ${es.hasta ? fechaLarga(es.hasta, idioma) : ''}`;
    if (!window.confirm(t(idioma, 'ficha.confirmarQuitarEstancia', { cuando }))) return;
    api
      .anularEstancia(propiedadId, es.id)
      .then(onCambio)
      .catch(() => undefined);
  };

  const registrar = (e: React.FormEvent) => {
    e.preventDefault();
    if (!desde || !hasta) return;
    setEnviando(true);
    api
      .crearEstancia(propiedadId, {
        desde,
        hasta,
        ...(huesped.trim() ? { huesped: huesped.trim() } : {}),
      })
      .then(() => {
        setDesde('');
        setHasta('');
        setHuesped('');
        onCambio();
      })
      .catch(() => undefined)
      .finally(() => setEnviando(false));
  };

  /*
   * La casa esta ocupada si hay una estancia SIN fecha de salida. No se guarda
   * un interruptor aparte a proposito: dos sitios donde mirar si la casa esta
   * ocupada acabarian diciendo cosas distintas.
   */
  const abierta = estancias.find((es) => es.hasta === null && es.estado !== 'CANCELADA');
  const ocupada = abierta !== undefined;

  const marcar = (quiereOcupada: boolean) => {
    if (quiereOcupada === ocupada) return;
    setEnviando(true);
    api
      .fijarOcupacion(propiedadId, quiereOcupada)
      .then(onCambio)
      .catch(() => undefined)
      .finally(() => setEnviando(false));
  };

  return (
    <section className="tarjeta">
      <div className="tarjeta-cabeza">
        <h2>{t(idioma, 'ficha.estancias')}</h2>
        {/*
          Dos botones a la vista y no un interruptor: el estado actual se lee sin
          interpretar de que lado esta la palanca, y el cambio es un solo gesto.
        */}
        <div className="cobro-estados" role="group" aria-label={t(idioma, 'ficha.estancias')}>
          <button
            type="button"
            className="cobro-boton verde"
            aria-pressed={ocupada}
            disabled={!puedeCrear || enviando || ocupada}
            onClick={() => marcar(true)}
          >
            {t(idioma, 'ficha.ocupada')}
          </button>
          <button
            type="button"
            className="cobro-boton"
            aria-pressed={!ocupada}
            disabled={!puedeCrear || enviando || !ocupada}
            onClick={() => marcar(false)}
          >
            {t(idioma, 'ficha.casaVacia')}
          </button>
        </div>
      </div>

      {estancias.length === 0 ? (
        <p className="suave">{t(idioma, 'ficha.sinEstancias')}</p>
      ) : (
        <div className="lista">
          {estancias.map((es) => (
            <div key={es.id} className="fila">
              <span className="crecer">
                <b>
                  {fechaLarga(es.desde, idioma)} → {es.hasta ? fechaLarga(es.hasta, idioma) : '…'}
                </b>
                {es.huesped && <small>{es.huesped}</small>}
                {es.hasta === null && <small>{t(idioma, 'ficha.sinSalida')}</small>}
              </span>
              {es.hasta === null && (
                <span className="pastilla verde">{t(idioma, 'ficha.ocupada')}</span>
              )}
              {/*
                La que sigue en curso no se quita: se cierra con «Casa vacia»,
                que es lo que de verdad ha pasado.
              */}
              {puedeAnular && es.hasta !== null && (
                <button type="button" className="enlace" onClick={() => anular(es)}>
                  {t(idioma, 'checklists.eliminar')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {puedeCrear && (
        <form className="formulario" onSubmit={registrar}>
          <div className="rejilla dos">
            <div className="campo">
              <label htmlFor="est-desde">{t(idioma, 'ficha.llegada')}</label>
              <input
                id="est-desde"
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
              />
            </div>
            <div className="campo">
              <label htmlFor="est-hasta">{t(idioma, 'ficha.salida')}</label>
              <input
                id="est-hasta"
                type="date"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
              />
            </div>
          </div>
          <div className="campo">
            <label htmlFor="est-huesped">{t(idioma, 'ficha.huesped')}</label>
            <input
              id="est-huesped"
              type="text"
              value={huesped}
              onChange={(e) => setHuesped(e.target.value)}
              maxLength={200}
            />
          </div>
          <button type="submit" disabled={enviando || !desde || !hasta}>
            + {t(idioma, 'ficha.registrarEstancia')}
          </button>
        </form>
      )}
    </section>
  );
}

// --- Fotos -------------------------------------------------------------------

function Fotos({
  idioma,
  propiedadId,
  fotos,
  puedeGestionar,
  onCambio,
}: {
  idioma: Idioma;
  propiedadId: string;
  fotos: FotoPropiedad[];
  puedeGestionar: boolean;
  onCambio: () => void;
}) {
  const [url, setUrl] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [falloFoto, setFalloFoto] = useState<string | null>(null);

  return (
    <section className="tarjeta">
      <div className="tarjeta-cabeza">
        <h2>
          {t(idioma, 'ficha.fotos')} ({fotos.length})
        </h2>
        {puedeGestionar && (
          <button type="button" className="enlace" onClick={() => setAbierto((v) => !v)}>
            + {t(idioma, 'ficha.anadirFoto')}
          </button>
        )}
      </div>

      {fotos.length === 0 && <p className="suave">{t(idioma, 'ficha.sinFotos')}</p>}
      {fotos.length > 0 && (
        <div className="rejilla tarjetas">
          {fotos.map((f) => (
            <figure key={f.id} style={{ margin: 0 }}>
              <img
                src={f.url}
                alt={f.titulo ?? ''}
                style={{ width: '100%', height: 170, objectFit: 'cover', borderRadius: '0.5rem' }}
              />
              {puedeGestionar && (
                <button
                  type="button"
                  className="enlace"
                  onClick={() => {
                    void api.eliminarFoto(propiedadId, f.id).then(onCambio);
                  }}
                >
                  {t(idioma, 'checklists.eliminar')}
                </button>
              )}
            </figure>
          ))}
        </div>
      )}

      {abierto && puedeGestionar && (
        <div className="formulario">
          {/*
            Subir el fichero es lo normal —la foto que se acaba de hacer con el
            movil—; el enlace externo queda debajo, como alternativa, para una
            galeria que ya vive en otro sitio.
          */}
          <div className="campo">
            {/*
              El selector nativo se esconde detras de un boton: su etiqueta dice
              «No se ha seleccionado ningun archivo» y, al vaciarlo despues de
              elegir, vuelve a decirlo. Se lee como si no hubiera pasado nada.
              Aqui manda el nombre del fichero y el estado de la subida.
            */}
            <label className="acceso-entrar boton-fichero">
              {subiendo ? t(idioma, 'ficha.subiendoFoto') : t(idioma, 'ficha.subirFoto')}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style={{ display: 'none' }}
                disabled={subiendo}
                onChange={(e) => {
                  const fichero = e.target.files?.[0];
                  if (!fichero) return;
                  setSubiendo(true);
                  setFalloFoto(null);
                  void api
                    .subirAdjunto('PROPIEDAD', propiedadId, fichero)
                    .then((r) => api.anadirFoto(propiedadId, { adjuntoId: r.adjunto.id }))
                    .then(() => {
                      setAbierto(false);
                      onCambio();
                    })
                    .catch((fallo: unknown) => {
                      // Se ENSENA. Tragarse esto es lo que hacia que subir una
                      // foto pareciera no hacer nada en absoluto.
                      setFalloFoto(
                        fallo instanceof ErrorApi
                          ? t(idioma, fallo.mensajeClave)
                          : t(idioma, 'error.servidor'),
                      );
                    })
                    .finally(() => {
                      setSubiendo(false);
                      // Se vacia AL FINAL, para poder reintentar con el mismo
                      // fichero sin que la etiqueta mienta mientras tanto.
                      e.target.value = '';
                    });
                }}
              />
            </label>
            <p className="suave" style={{ fontSize: '0.8125rem' }}>
              {t(idioma, 'ficha.subirFotoAyuda')}
            </p>
            {falloFoto && (
              <p className="error" role="alert">
                {falloFoto}
              </p>
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!url.trim()) return;
              void api
                .anadirFoto(propiedadId, { url: url.trim() })
                .then(() => {
                  setUrl('');
                  setAbierto(false);
                  onCambio();
                })
                .catch(() => undefined);
            }}
          >
            <div className="campo">
              <label htmlFor="foto-url">{t(idioma, 'ficha.urlFoto')}</label>
              <input
                id="foto-url"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://…"
              />
            </div>
            <button type="submit" className="secundario" disabled={!url.trim() || subiendo}>
              {t(idioma, 'ficha.anadirPorUrl')}
            </button>
          </form>
        </div>
      )}
    </section>
  );
}

// --- Mantenimiento -----------------------------------------------------------

function PanelMantenimiento({
  idioma,
  propiedadId,
  tareas,
  puedeGestionar,
  onCambio,
  onNueva,
}: {
  idioma: Idioma;
  propiedadId: string;
  tareas: Mantenimiento[] | null;
  puedeGestionar: boolean;
  onCambio: () => void;
  onNueva: () => void;
}) {
  return (
    <section className="tarjeta">
      <div className="tarjeta-cabeza">
        <h2>{t(idioma, 'ficha.mantenimiento')}</h2>
        {puedeGestionar && (
          <button type="button" onClick={onNueva}>
            + {t(idioma, 'mant.nueva')}
          </button>
        )}
      </div>
      <p className="suave" style={{ fontSize: '0.8125rem' }}>
        {t(idioma, 'ficha.mantenimientoExplicacion')}
      </p>

      {tareas === null && <p>{t(idioma, 'app.cargando')}</p>}
      {tareas !== null && tareas.length === 0 && (
        <p className="suave">{t(idioma, 'ficha.sinMantenimiento')}</p>
      )}
      {tareas !== null && tareas.length > 0 && (
        <div className="lista">
          {tareas.map((m) => (
            <div key={m.id} className="fila">
              <span className="chip-area">{t(idioma, `areaMant.${m.area}`)}</span>
              <span className="crecer">
                <b>{m.concepto}</b>
                <small>
                  {t(idioma, `frecuencia.${m.frecuencia}`)}
                  {m.frecuencia === 'PERSONALIZADA' && m.cadenciaDias
                    ? ` (${t(idioma, 'ficha.cadaDias', { total: m.cadenciaDias })})`
                    : ''}
                  {` · ${t(idioma, 'ficha.genera')}: ${t(idioma, `evento.${m.genera === 'INCIDENCIA' ? 'OTRO' : 'VISITA'}`)}`}
                  {m.proximaEn ? ` · ${t(idioma, 'ficha.proxima')}: ${m.proximaEn}` : ''}
                  {m.diasAntelacion > 0
                    ? ` · ${t(idioma, 'ficha.antelacion', { total: m.diasAntelacion })}`
                    : ''}
                  {m.providerNombre ? ` · ${m.providerNombre}` : ''}
                </small>
              </span>
              <span className={m.frecuencia === 'ANUAL' ? 'chip-foto' : 'chip-area'}>
                {t(idioma, `frecuencia.${m.frecuencia}`)}
              </span>
              {puedeGestionar && (
                <button
                  type="button"
                  className="enlace"
                  onClick={() => {
                    void api.mantenimientoHecho(propiedadId, m.id).then(onCambio);
                  }}
                >
                  {t(idioma, 'ficha.marcarHecho')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// --- Proveedores de la propiedad ---------------------------------------------

function PanelProveedoresPropiedad({
  idioma,
  propiedadId,
  vinculados,
  directorio,
  puedeGestionar,
  onCambio,
  onNuevo,
}: {
  idioma: Idioma;
  propiedadId: string;
  vinculados: ProveedorVinculado[] | null;
  directorio: Proveedor[];
  puedeGestionar: boolean;
  onCambio: () => void;
  onNuevo: () => void;
}) {
  const [elegido, setElegido] = useState('');
  const yaVinculados = new Set((vinculados ?? []).map((v) => v.providerId));
  const disponibles = directorio.filter((p) => p.activo && !yaVinculados.has(p.id));

  return (
    <section className="tarjeta">
      <div className="tarjeta-cabeza">
        <h2>{t(idioma, 'ficha.proveedores')}</h2>
        {puedeGestionar && (
          <button type="button" onClick={onNuevo}>
            + {t(idioma, 'prov.nuevo')}
          </button>
        )}
      </div>
      <p className="suave" style={{ fontSize: '0.8125rem' }}>
        {t(idioma, 'ficha.proveedoresExplicacion')}
      </p>

      {vinculados === null && <p>{t(idioma, 'app.cargando')}</p>}
      {vinculados !== null && vinculados.length === 0 && (
        <p className="suave">{t(idioma, 'ficha.sinProveedores')}</p>
      )}
      {vinculados !== null && vinculados.length > 0 && (
        <div className="lista">
          {vinculados.map((v) => (
            <div key={v.id} className="fila">
              <span className="pastilla info">{t(idioma, `oficio.${v.oficio}`)}</span>
              <span className="crecer">
                <b>{v.nombre}</b>
                <small>
                  {[v.email, v.telefono].filter(Boolean).join(' · ') ||
                    t(idioma, 'proveedores.sinContacto')}
                </small>
              </span>
              {puedeGestionar && (
                <button
                  type="button"
                  className="enlace"
                  onClick={() => {
                    void api.desvincularProveedor(propiedadId, v.providerId).then(onCambio);
                  }}
                >
                  {t(idioma, 'ficha.desvincular')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {puedeGestionar && disponibles.length > 0 && (
        <form
          className="formulario"
          onSubmit={(e) => {
            e.preventDefault();
            if (!elegido) return;
            void api.vincularProveedor(propiedadId, elegido).then(() => {
              setElegido('');
              onCambio();
            });
          }}
        >
          <div className="campo">
            <label htmlFor="prov-vincular">{t(idioma, 'ficha.vincularProveedor')}</label>
            <select id="prov-vincular" value={elegido} onChange={(e) => setElegido(e.target.value)}>
              <option value="">—</option>
              {disponibles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre} · {t(idioma, `oficio.${p.oficio}`)}
                </option>
              ))}
            </select>
          </div>
          <button type="submit" disabled={!elegido}>
            + {t(idioma, 'ficha.vincular')}
          </button>
        </form>
      )}
    </section>
  );
}

// --- Lista de incidencias con su detalle operativo ---------------------------

const CLASE_ESTADO_GASTO: Record<string, string> = {
  PENDIENTE_APROBACION: 'ambar',
  APROBADO: 'info',
  RECHAZADO: 'rojo',
  PAGADO: 'verde',
};

function ListaIncidencias({
  idioma,
  incidencias,
  dinero,
  puedeGestionar,
  puedeAnular,
  onAbrir,
  onCambio,
}: {
  idioma: Idioma;
  incidencias: Incidencia[];
  dinero: (c: number) => string;
  puedeGestionar: boolean;
  puedeAnular: boolean;
  onAbrir?: (id: string) => void;
  onCambio: () => void;
}) {
  const [error, setError] = useState<string | null>(null);

  const conError = (p: Promise<unknown>) => {
    setError(null);
    p.then(onCambio).catch((f: unknown) =>
      setError(f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'error.servidor')),
    );
  };

  /** Abierta ↔ En curso. No pide justificacion: empezar no afirma nada. */
  const cambiar = (i: Incidencia, enCurso: boolean) =>
    conError(api.incidenciaEnCurso(i.id, enCurso));

  /*
   * Cerrar SI pide texto, y por eso no cambia sola al pulsar: cerrar afirma que
   * el problema ya no esta, y esa afirmacion tiene que quedar escrita. El
   * servidor tampoco la acepta sin resolucion.
   */
  const cerrar = (i: Incidencia) => {
    const resolucion = window.prompt(t(idioma, 'inc.pedirResolucion'));
    if (resolucion === null || resolucion.trim().length < 3) return;
    conError(api.cerrarIncidencia(i.id, resolucion.trim()));
  };

  const anular = (i: Incidencia) => {
    const motivo = window.prompt(t(idioma, 'inc.pedirMotivo'));
    if (motivo === null || motivo.trim().length < 3) return;
    conError(api.anularIncidencia(i.id, motivo.trim()));
  };

  if (incidencias.length === 0) {
    return (
      <section className="tarjeta">
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <p className="suave">{t(idioma, 'ficha.sinIncidencias')}</p>
      </section>
    );
  }
  return (
    <div className="lista-incidencias">
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {incidencias.map((i) => (
        <article
          key={i.id}
          className={`tarjeta${onAbrir ? ' tarjeta-pulsable' : ''}`}
          onClick={onAbrir ? () => onAbrir(i.id) : undefined}
        >
          <div className="fila-flex" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
            <span className={`chip-nivel N${i.nivel}`}>
              N{i.nivel} — {t(idioma, `nivelIncidencia.${i.nivel}`)}
            </span>
            <span className="chip-area">{t(idioma, `areaMant.${i.area}`)}</span>
            {i.slaIncumplido && (
              <span className="chip-sla" title={t(idioma, 'ficha.slaIncumplido')}>
                <Icono nombre="cronometro" /> {t(idioma, 'ficha.slaIncumplido')}{' '}
                {i.horasTranscurridas}h/{i.slaHoras}h
              </span>
            )}
            {/*
              El estado se cambia AQUI, sin abrir la incidencia. Es el gesto
              que mas se repite —se atiende una y se pasa a la siguiente— y
              obligar a entrar y volver por cada una lo convierte en una tarea.

              `stopPropagation` porque la tarjeta entera abre la incidencia: sin
              el, cambiar el estado abriria ademas el detalle.
            */}
            {puedeGestionar && incidenciaActiva(i.estado) ? (
              <span
                className="cobro-estados"
                role="group"
                style={{ marginLeft: 'auto' }}
                aria-label={t(idioma, 'inc.estado')}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="cobro-boton"
                  aria-pressed={i.estado === 'OPEN'}
                  disabled={i.estado === 'OPEN'}
                  onClick={() => cambiar(i, false)}
                >
                  {t(idioma, 'estadoIncidencia.OPEN')}
                </button>
                <button
                  type="button"
                  className="cobro-boton"
                  aria-pressed={i.estado === 'IN_PROGRESS'}
                  disabled={i.estado === 'IN_PROGRESS'}
                  onClick={() => cambiar(i, true)}
                >
                  {t(idioma, 'estadoIncidencia.IN_PROGRESS')}
                </button>
                <button
                  type="button"
                  className="cobro-boton"
                  aria-pressed={false}
                  onClick={() => cerrar(i)}
                >
                  {t(idioma, 'estadoIncidencia.CLOSED')}
                </button>
              </span>
            ) : (
              <span
                className={`pastilla ${incidenciaActiva(i.estado) ? 'ambar' : 'verde'}`}
                style={{ marginLeft: 'auto' }}
              >
                {t(idioma, `estadoIncidencia.${i.estado}`)}
              </span>
            )}
          </div>
          <div className="fila-flex" style={{ gap: '0.75rem', alignItems: 'flex-start' }}>
            {i.portadaAdjuntoId && (
              /*
                Si el fichero ya no esta —o el navegador no sabe pintar ese
                formato, como pasa con las fotos HEIC del iPhone fuera de
                Apple— la miniatura se retira entera. Un cuadro roto se lee
                como «la aplicacion falla»; sin miniatura, simplemente no hay
                miniatura.
              */
              <img
                src={api.urlAdjunto(i.portadaAdjuntoId)}
                alt=""
                className="miniatura-incidencia"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            <div style={{ minWidth: 0 }}>
              <b>{i.titulo}</b>
              {i.descripcion && <p className="suave">{i.descripcion}</p>}
            </div>
          </div>
          <div className="fila-flex" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
            {i.costeEstimadoCents !== null && (
              <span className="numerico">
                {dinero(i.costeEstimadoCents)} {t(idioma, 'ficha.estimado')}
              </span>
            )}
            {i.costeFinalCents !== null && (
              <span className="numerico">
                {dinero(i.costeFinalCents)} {t(idioma, 'ficha.final')}
              </span>
            )}
            {i.proveedorNombre && <span className="suave">{i.proveedorNombre}</span>}
            {puedeAnular && incidenciaActiva(i.estado) && (
              <button
                type="button"
                className="enlace pequeno"
                onClick={(e) => {
                  e.stopPropagation();
                  anular(i);
                }}
              >
                {t(idioma, 'incidencia.anular')}
              </button>
            )}
            <span className="pequeno suave numerico" style={{ marginLeft: 'auto' }}>
              {fechaLarga(i.creadoEn, idioma)}
            </span>
          </div>
        </article>
      ))}
    </div>
  );
}

// --- Documentos de la propiedad ----------------------------------------------

function PanelDocumentos({
  idioma,
  propiedadId,
  documentos,
  puedeGestionar,
  onCambio,
  onSubir,
}: {
  idioma: Idioma;
  propiedadId: string;
  documentos: DocumentoPropiedad[] | null;
  puedeGestionar: boolean;
  onCambio: () => void;
  onSubir: () => void;
}) {
  const [error, setError] = useState<string | null>(null);

  /*
   * Un contrato se quita con confirmacion y diciendo cual. Es el unico sitio de
   * la ficha donde se borra un fichero de verdad, y equivocarse de fila aqui no
   * se deshace.
   */
  const quitar = (d: DocumentoPropiedad) => {
    if (!window.confirm(t(idioma, 'ficha.confirmarQuitarDocumento', { titulo: d.titulo }))) return;
    setError(null);
    api
      .eliminarDocumento(propiedadId, d.id)
      .then(onCambio)
      .catch((f: unknown) =>
        setError(f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'error.servidor')),
      );
  };

  return (
    <section className="tarjeta">
      <div className="tarjeta-cabeza">
        <h2>{t(idioma, 'ficha.documentos')}</h2>
        {puedeGestionar && (
          <button type="button" onClick={onSubir}>
            <Icono nombre="importar" /> {t(idioma, 'ficha.subirDocumento')}
          </button>
        )}
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {documentos === null && <p>{t(idioma, 'app.cargando')}</p>}
      {documentos !== null && documentos.length === 0 && (
        <p className="suave">{t(idioma, 'ficha.sinDocumentos')}</p>
      )}
      {documentos !== null && documentos.length > 0 && (
        <div className="lista">
          {documentos.map((d) => {
            /*
              ABRIRLO ES TOCARLO, NO BUSCAR UN ENLACE PEQUENO AL FINAL.

              El nombre del documento ES el enlace y ocupa toda la fila, que es
              lo que se toca cuando se quiere abrir un contrato. Abrir el
              fichero subido y abrir una direccion externa son la misma accion
              para quien mira: cambia la fuente, no el gesto.

              Y cuando no hay fichero se DICE. Antes no aparecia nada y la fila
              simplemente no respondia: parecia estropeada. Hay documentos que
              solo tienen nombre —los que entraron en la importacion sin su
              PDF—, y saberlo es lo que permite volver a subirlo.
            */
            const destino = d.adjuntoId ? api.urlAdjunto(d.adjuntoId) : d.url;
            return (
              <div key={d.id} className="fila">
                <Icono nombre="documento" />
                <span className="crecer">
                  {destino ? (
                    <a href={destino} target="_blank" rel="noreferrer" className="enlace">
                      {d.titulo}
                    </a>
                  ) : (
                    <b>{d.titulo}</b>
                  )}
                  <small>
                    {[d.nombreFichero, fechaLarga(d.creadoEn, idioma)].filter(Boolean).join(' · ')}
                    {!destino && ` · ${t(idioma, 'ficha.sinFichero')}`}
                  </small>
                </span>
                <span className="chip-area">{t(idioma, `tipoDocumento.${d.tipo}`)}</span>
                {/*
                  LO QUE VE SU DUEÑO. Lo que se sube aquí son los papeles de SU
                  casa y por eso se comparten de salida; esto está para la
                  excepción —un informe interno—, no para el caso normal.
                */}
                {puedeGestionar && (
                  <label className="interruptor-doc">
                    <input
                      type="checkbox"
                      checked={d.visibleCliente}
                      onChange={(e) => {
                        void api
                          .compartirDocumento(propiedadId, d.id, e.target.checked)
                          .then(onCambio)
                          .catch(() => undefined);
                      }}
                    />
                    <span>{t(idioma, 'ficha.verDueno')}</span>
                  </label>
                )}
                {destino && (
                  <a href={destino} target="_blank" rel="noreferrer" className="enlace">
                    {t(idioma, 'ficha.abrir')}
                  </a>
                )}
                {puedeGestionar && (
                  <button type="button" className="enlace" onClick={() => quitar(d)}>
                    {t(idioma, 'checklists.eliminar')}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* El ciclo de una visita: Abierta → En curso → Cerrada, y Cancelada aparte. */
const ESTADOS_VISITA: EstadoEvento[] = ['PREVISTA', 'EN_CURSO', 'HECHA', 'CANCELADA'];

const CLASE_COBRO: Record<string, string> = {
  POR_COBRAR: 'ambar',
  FACTURADO: 'info',
  COBRADO: 'verde',
};

const ESTADOS_COBRO: EstadoCobro[] = ['POR_COBRAR', 'FACTURADO', 'COBRADO'];

/**
 * En que punto esta el dinero de un gasto adelantado.
 *
 * Son TRES botones a la vista y no un desplegable: es lo que mas se toca de un
 * gasto, y un desplegable esconde el estado actual detras de un clic. Ademas se
 * lee de un vistazo cuantos hay en cada punto sin abrir nada.
 */
function Cobro({
  idioma,
  gasto,
  onCambio,
}: {
  idioma: Idioma;
  gasto: GastoPropiedad;
  onCambio: () => void;
}) {
  const [ocupado, setOcupado] = useState(false);
  return (
    <div className="cobro-estados" role="group" aria-label={t(idioma, 'cobro.titulo')}>
      {ESTADOS_COBRO.map((estado) => (
        <button
          key={estado}
          type="button"
          className={`cobro-boton ${CLASE_COBRO[estado]}`}
          aria-pressed={gasto.estadoCobro === estado}
          disabled={ocupado || gasto.estadoCobro === estado}
          onClick={() => {
            setOcupado(true);
            void api
              .marcarCobroGasto(gasto.id, estado)
              .then(onCambio)
              .catch(() => undefined)
              .finally(() => setOcupado(false));
          }}
        >
          {t(idioma, `cobro.${estado}`)}
        </button>
      ))}
    </div>
  );
}
